import type { ControlWidth, FieldDef, FormatterFn } from './types';
import { getDataSource } from '../registries/asyncSelectRegistry';
import { resolveControlWidth } from '../resolveControlWidth';

export interface FormFieldProps {
  label: string;
  placeholder?: string;
  hint?: string;
  formatter?: FormatterFn;
  controlWidth?: ControlWidth;
  dataSource?: ReturnType<typeof getDataSource>;
}

function composeFormatterPipeline(fns: FormatterFn[]): FormatterFn {
  return (value: string) => fns.reduce((v, fn) => fn(v), value);
}

/**
 * Extracts adapter-ready props from a domain FieldDef.
 *
 * For `adapterHint: 'async-select'` fields, this auto-attaches `dataSource`
 * from the asyncSelectRegistry — forms never need to write `dataSource={...}`
 * manually. The URL is defined once in the registry, resolved here automatically.
 *
 * Form-level wiring (`dataSourceParams`, `effects`, `resetOnParamsChange`)
 * is still passed explicitly on the adapter — only the DataSource connection
 * itself is auto-resolved.
 */
export function formProps(def: FieldDef): FormFieldProps {
  const props: FormFieldProps = { label: def.label };
  if (def.placeholder) props.placeholder = def.placeholder;
  if (def.hint) props.hint = def.hint;
  if (def.formatter) {
    props.formatter = Array.isArray(def.formatter)
      ? composeFormatterPipeline(def.formatter)
      : def.formatter;
  }

  if (def.adapterHint === 'async-select') {
    const ds = getDataSource(def);
    if (ds) props.dataSource = ds;
  }

  return props;
}
